import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { stats } from "../mock/mock";
import api from "../lib/api";
import { HERO_BG_URL } from "../lib/assets";
import PlayerCard from "../components/PlayerCard";
import { Button } from "../components/ui/button";
import { Search, Users, UserPlus, Video, Eye, Handshake, ArrowRight, GraduationCap, ShieldCheck, User } from "lucide-react";

const Home = () => {
  const { t } = useApp();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    api.get("/players/featured").then(({ data }) => setPlayers(data || [])).catch(() => {});
  }, []);

  const roleCards = [
    { icon: User, ...t.join.player, color: "from-blue-500/20 to-blue-600/5", ring: "border-blue-500/30" },
    { icon: GraduationCap, ...t.join.academy, color: "from-emerald-500/20 to-emerald-600/5", ring: "border-emerald-500/30" },
    { icon: ShieldCheck, ...t.join.scout, color: "from-orange-500/20 to-orange-600/5", ring: "border-orange-500/30" },
  ];

  const howSteps = [
    { icon: UserPlus, title: t.how.s1t, desc: t.how.s1d },
    { icon: Video, title: t.how.s2t, desc: t.how.s2d },
    { icon: Eye, title: t.how.s3t, desc: t.how.s3d },
    { icon: Handshake, title: t.how.s4t, desc: t.how.s4d },
  ];

  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.18]"
          style={{ backgroundImage: `url(${HERO_BG_URL})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08090b]/70 via-[#08090b]/60 to-[#08090b]" />
        <div className="absolute inset-0 hero-glow" />
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="relative max-w-4xl mx-auto px-5 pt-24 pb-24 text-center">
          <span className="inline-block text-xs font-medium text-orange-300/90 border border-orange-500/25 bg-orange-500/10 rounded-full px-4 py-1.5 mb-8 animate-fade-up">
            {t.hero.badge}
          </span>
          <h1 className="font-display text-4xl sm:text-6xl font-bold leading-[1.08] tracking-tight animate-fade-up">
            <span className="text-white">{t.hero.title1} </span>
            <span className="bg-gradient-to-r from-blue-400 to-blue-500 bg-clip-text text-transparent">{t.hero.title2} </span>
            <span className="text-white">{t.hero.title3}</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-gray-400 max-w-2xl mx-auto animate-fade-up">
            {t.hero.subtitle}
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3 animate-fade-up">
            <Button onClick={() => navigate("/register")} className="bg-blue-600 hover:bg-blue-500 text-white rounded-full h-12 px-7 text-[15px] font-medium w-full sm:w-auto">
              <Users className="w-4 h-4 mr-2" /> {t.hero.cta1}
            </Button>
            <Button onClick={() => navigate("/search")} variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 text-white rounded-full h-12 px-7 text-[15px] w-full sm:w-auto">
              <Search className="w-4 h-4 mr-2" /> {t.hero.cta2}
            </Button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-white/5 bg-[#0a0c10]">
        <div className="max-w-4xl mx-auto px-5 py-12 grid grid-cols-3 gap-6 text-center">
          {[
            { value: players.length || stats.players, label: t.stats.players },
            { value: stats.academies || "—", label: t.stats.academies },
            { value: stats.scouts || "—", label: t.stats.scouts },
          ].map((s, i) => (
            <div key={i}>
              <div className="font-display text-3xl sm:text-4xl font-bold text-white">{s.value}</div>
              <div className="text-xs sm:text-sm text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Join */}
      <section className="max-w-6xl mx-auto px-5 py-24">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">{t.join.title}</h2>
          <p className="text-gray-400 mt-3">{t.join.subtitle}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {roleCards.map((r, i) => (
            <div key={i} className={`card-hover bg-[#0f1116] border ${r.ring} rounded-2xl p-7`}>
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${r.color} border border-white/10 flex items-center justify-center mb-5`}>
                <r.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-white text-lg">{r.title}</h3>
              <p className="text-sm text-gray-400 mt-2 leading-relaxed">{r.desc}</p>
              <button onClick={() => navigate("/register")} className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors">
                {r.cta} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#0a0c10] border-y border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-24">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white text-center mb-14">{t.how.title}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howSteps.map((s, i) => (
              <div key={i} className="relative">
                <div className="text-[64px] font-display font-bold text-white/5 leading-none absolute -top-4 right-2 select-none">{i + 1}</div>
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-4">
                    <s.icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-white">{s.title}</h3>
                  <p className="text-sm text-gray-400 mt-2 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured players */}
      <section className="max-w-6xl mx-auto px-5 py-24">
        <div className="flex items-center justify-between mb-10">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">{t.featured.title}</h2>
          <button onClick={() => navigate("/search")} className="text-sm font-medium text-blue-400 hover:text-blue-300 inline-flex items-center gap-1.5">
            {t.featured.viewAll} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {players.map((p) => (
            <PlayerCard key={p.id} player={p} />
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-5xl mx-auto px-5 pb-24">
        <div className="relative overflow-hidden rounded-3xl border border-blue-500/20 bg-gradient-to-br from-blue-600/15 via-[#0f1116] to-[#0f1116] p-12 text-center">
          <div className="absolute inset-0 grid-lines opacity-30" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">{t.finalCta.title}</h2>
            <p className="text-gray-400 mt-4 max-w-xl mx-auto">{t.finalCta.subtitle}</p>
            <Button onClick={() => navigate("/register")} className="mt-8 bg-blue-600 hover:bg-blue-500 text-white rounded-full h-12 px-8 text-[15px] font-medium">
              {t.finalCta.cta}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
